import type { LevelDef } from '../sim/level/schema';

/**
 * Arenas for 5–8 fighters keep a continuous ground route and two approaches to the high ground.
 * Spawns alternate between open ground and ledges, with at least four metres between centres.
 * Fixed routes survive destroyed cover; the shared camera only needs 44–48 metres of width.
 */
export const canopyCommons: LevelDef = {
  id: 'woods-canopy-commons',
  name: 'Canopy Commons',
  theme: 'woods',
  bounds: { x: 0, y: 0, w: 44, h: 20 },
  killMargin: 6,
  spawns: [
    { x: 3, y: 3.5 },
    { x: 41, y: 3.5 },
    { x: 8, y: 6.1 },
    { x: 36, y: 6.1 },
    { x: 17, y: 3.5 },
    { x: 27, y: 3.5 },
    { x: 15, y: 8.5 },
    { x: 29, y: 8.5 },
  ],
  drops: { enabled: true, xMin: 5, xMax: 39, intervalScale: 1 },
  objects: [
    { type: 'solid', x: 22, y: 1, w: 44, h: 2 },
    // Branches form two rising routes into the central canopy; the ground remains a shortcut.
    { type: 'solid', x: 8, y: 4.4, w: 6, h: 0.4 },
    { type: 'solid', x: 36, y: 4.4, w: 6, h: 0.4 },
    { type: 'solid', x: 15, y: 6.8, w: 5, h: 0.4 },
    { type: 'solid', x: 29, y: 6.8, w: 5, h: 0.4 },
    // A rope crossing can lose individual spans; both branch routes and the forest floor survive.
    { type: 'solid', x: 18, y: 9.42, w: 2, h: 0.4 },
    { type: 'solid', x: 26, y: 9.42, w: 2, h: 0.4 },
    ...[20, 22, 24].map((x) => ({ type: 'block.destructible', x, y: 9.42, w: 2, h: 0.4, hp: 90 })),
    // A jumpable root breaks the long firing lane without blocking the upper route.
    { type: 'solid', x: 22, y: 2.9, w: 2, h: 1.8 },
    { type: 'solid', x: 12.1, y: 2.6, w: 1.5, h: 1.2 },
    { type: 'solid', x: 31.9, y: 2.6, w: 1.5, h: 1.2 },
    { type: 'crate', x: 14, y: 2.7, w: 1.4, h: 1.4 },
    { type: 'crate', x: 30, y: 2.7, w: 1.4, h: 1.4 },
  ],
  decor: [
    { kind: 'tree', x: 4, y: 2, scale: 1.6 },
    { kind: 'tree', x: 40, y: 2, scale: 1.6 },
    { kind: 'heart-tree', x: 22, y: 2, scale: 1 },
  ],
};

export const freightExchange: LevelDef = {
  id: 'factory-freight-exchange',
  name: 'Freight Exchange',
  theme: 'factory',
  bounds: { x: 0, y: 0, w: 48, h: 20 },
  killMargin: 6,
  spawns: [
    { x: 2, y: 3.5 },
    { x: 46, y: 3.5 },
    { x: 6, y: 6.1 },
    { x: 42, y: 6.1 },
    { x: 22, y: 3.8 },
    { x: 26, y: 3.8 },
    { x: 17, y: 6.1 },
    { x: 31, y: 6.1 },
  ],
  drops: { enabled: true, xMin: 5, xMax: 43, intervalScale: 1 },
  objects: [
    { type: 'solid', x: 24, y: 1, w: 48, h: 2 },
    // Loading docks and overhead gantries alternate: every bay has an escape above and below.
    { type: 'solid', x: 6, y: 4.4, w: 6, h: 0.4 },
    { type: 'solid', x: 17, y: 4.4, w: 6, h: 0.4 },
    { type: 'solid', x: 31, y: 4.4, w: 6, h: 0.4 },
    { type: 'solid', x: 42, y: 4.4, w: 6, h: 0.4 },
    { type: 'solid', x: 12, y: 7, w: 6, h: 0.4 },
    { type: 'solid', x: 20, y: 7, w: 3, h: 0.4 },
    { type: 'solid', x: 28, y: 7, w: 3, h: 0.4 },
    { type: 'platform.moving', x: 24, y: 6.85, w: 4, h: 0.4, speed: 1.6, path: [{ x: 24, y: 4.8 }, { x: 24, y: 8.9 }] },
    { type: 'solid', x: 36, y: 7, w: 6, h: 0.4 },
    { type: 'conveyor', x: 24, y: 2.15, w: 8, h: 0.3, speed: 2 },
    { type: 'crate', x: 10.5, y: 2.75, w: 1.5, h: 1.5 },
    { type: 'crate', x: 37.5, y: 2.75, w: 1.5, h: 1.5 },
    // Bay walls interrupt ground fire while leaving access to the lift on both sides.
    { type: 'solid', x: 21, y: 2.7, w: 1.5, h: 1.4 },
    { type: 'solid', x: 27, y: 2.7, w: 1.5, h: 1.4 },
  ],
  decor: [
    { kind: 'stack', x: 3, y: 2, scale: 1.4 },
    { kind: 'stack', x: 45, y: 2, scale: 1.4 },
    { kind: 'cargo-frame', x: 24, y: 2, scale: 1 },
  ],
};

export const crownCourtyard: LevelDef = {
  id: 'castle-crown-courtyard',
  name: 'Crown Courtyard',
  theme: 'castle',
  bounds: { x: 0, y: 0, w: 46, h: 20 },
  killMargin: 6,
  spawns: [
    { x: 10, y: 3.5 },
    { x: 36, y: 3.5 },
    { x: 4, y: 7.1 },
    { x: 42, y: 7.1 },
    { x: 20, y: 3.5 },
    { x: 26, y: 3.5 },
    { x: 15, y: 6.1 },
    { x: 31, y: 6.1 },
  ],
  drops: { enabled: true, xMin: 5, xMax: 41, intervalScale: 1 },
  objects: [
    { type: 'solid', x: 23, y: 1, w: 46, h: 2 },
    // Low stepped battlements protect the ends but have no pits or one-way dead ends.
    { type: 'solid', x: 4, y: 2.9, w: 6, h: 1.8 },
    { type: 'solid', x: 42, y: 2.9, w: 6, h: 1.8 },
    { type: 'solid', x: 4, y: 4.7, w: 3, h: 1.8 },
    { type: 'solid', x: 42, y: 4.7, w: 3, h: 1.8 },
    { type: 'solid', x: 15, y: 4.4, w: 6, h: 0.4 },
    { type: 'solid', x: 31, y: 4.4, w: 6, h: 0.4 },
    { type: 'solid', x: 9, y: 7, w: 3, h: 0.4 },
    { type: 'solid', x: 37, y: 7, w: 3, h: 0.4 },
    ...[20, 23, 26].map((x) => ({ type: 'block.destructible', x, y: 6.8, w: 3, h: 0.5, hp: 130 })),
    // The monument splits the courtyard's sightline; both galleries reach the bridge above it.
    { type: 'solid', x: 23, y: 2.9, w: 2, h: 1.8 },
    { type: 'block.destructible', x: 8.5, y: 2.65, w: 1.3, h: 1.3, hp: 60 },
    { type: 'block.destructible', x: 37.5, y: 2.65, w: 1.3, h: 1.3, hp: 60 },
  ],
  decor: [
    { kind: 'tower', x: 4, y: 2, scale: 1.4 },
    { kind: 'tower', x: 42, y: 2, scale: 1.4 },
    { kind: 'moon', x: 23, y: 16, scale: 0.9 },
    { kind: 'courtyard-arch', x: 23, y: 2, scale: 1 },
  ],
};

export const LARGE_ARENAS: LevelDef[] = [canopyCommons, freightExchange, crownCourtyard];
